import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useDebounced } from "@/lib/useDebounced";
import { toast } from "sonner";
import { UserCheck, User, Camera, X } from "lucide-react";

export default function NewEntry() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [reg, setReg] = useState("");
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState("");
  const [foundVehicle, setFoundVehicle] = useState(null);
  const [custQ, setCustQ] = useState("");
  const debouncedCustQ = useDebounced(custQ, 300);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [walkin, setWalkin] = useState({ driver_name: "", company_name: "", phone: "" });
  const [quantity, setQuantity] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [regPhoto, setRegPhoto] = useState(null); // { file, previewUrl }
  const fileRef = useRef(null);
  const isStaff = user?.role === "truckstop_staff";

  useEffect(() => {
    api.get("/service-types", { params: { active_only: true, limit: 100 } })
      .then(({ data }) => {
        setServices(data.items);
        if (data.items.length > 0) setServiceId(data.items[0].id);
      });
  }, []);

  useEffect(() => {
    if (isStaff) return; // staff cannot search/select customers freely; still handled via same UI but backend blocks creation
    if (selectedCustomer) return;
    if (!debouncedCustQ || debouncedCustQ.length < 2) { setCustomerOptions([]); return; }
    let cancelled = false;
    api.get("/customers", { params: { q: debouncedCustQ, limit: 8 } })
       .then(({ data }) => { if (!cancelled) setCustomerOptions(data.items); })
       .catch(() => { if (!cancelled) setCustomerOptions([]); });
    return () => { cancelled = true; };
  }, [debouncedCustQ, selectedCustomer, isStaff]);

  const searchReg = async () => {
    const q = reg.trim();
    if (!q) return;
    try {
      const { data } = await api.get("/vehicles/search", { params: { q } });
      if (data.items.length > 0) {
        const v = data.items[0];
        setFoundVehicle(v);
        if (v.customer && !selectedCustomer) setSelectedCustomer(v.customer);
      } else {
        setFoundVehicle(null);
      }
    } catch (e) { toast.error(formatError(e, "Search failed")); }
  };

  const pickCustomer = (c) => {
    setSelectedCustomer(c);
    setCustQ("");
    setCustomerOptions([]);
  };
  const clearCustomer = () => { setSelectedCustomer(null); };

  const onSelectPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (regPhoto?.previewUrl) URL.revokeObjectURL(regPhoto.previewUrl);
    setRegPhoto({ file: f, previewUrl: URL.createObjectURL(f) });
  };
  const clearPhoto = () => {
    if (regPhoto?.previewUrl) URL.revokeObjectURL(regPhoto.previewUrl);
    setRegPhoto(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!serviceId) return toast.error("Pick a service type first");
    setSubmitting(true);
    let created = null;
    try {
      const body = {
        registration_no: reg.trim(),
        service_type_id: serviceId,
        quantity: quantity ? parseFloat(quantity) : null,
        notes: notes || null,
      };
      if (selectedCustomer) {
        body.customer_account_id = selectedCustomer.id;
      } else {
        const anyWalkin = walkin.driver_name || walkin.company_name || walkin.phone;
        if (anyWalkin) body.walkin_snapshot = walkin;
      }
      const { data } = await api.post("/orders", body);
      created = data;

      // Attach registration photo if the user selected one — best-effort.
      // If it fails, the order still exists; user can retry from Order Detail.
      if (regPhoto?.file) {
        try {
          const fd = new FormData();
          fd.append("file", regPhoto.file);
          await api.post(`/orders/${data.id}/registration-photo`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (photoErr) {
          toast.warning(
            "Order opened, but the registration photo failed to upload. " +
            "You can retry from the order page.",
            { description: formatError(photoErr, "") }
          );
        }
      }

      toast.success(`Order ${data.order_no} opened`);
      nav(`/app/orders/${data.id}`);
    } catch (err) {
      toast.error(formatError(err, "Failed to open order"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-4xl">
      <div className="mb-6">
        <div className="label-overline">Operations</div>
        <h1 className="text-3xl font-black tracking-tight mt-1 text-slate-900">New Truck Entry</h1>
        <p className="text-slate-600 text-sm mt-1">
          Entry time is captured automatically when you open the order.
        </p>
      </div>

      <form onSubmit={submit} className="bg-white border border-slate-200 rounded p-6 space-y-6">
        {/* Registration */}
        <div>
          <Label className="label-overline">Registration</Label>
          <div className="flex gap-2 mt-1">
            <Input data-testid="ne-registration"
                   className="rounded h-12 font-mono text-lg uppercase tracking-widest"
                   required maxLength={20}
                   value={reg} onChange={(e) => setReg(e.target.value.toUpperCase())} />
            <Button type="button" data-testid="ne-search-btn"
                    className="rounded h-12 bg-slate-900 text-white hover:bg-slate-800"
                    onClick={searchReg}>Search</Button>
          </div>
          {foundVehicle && (
            <div className="mt-2 text-xs bg-blue-50 border border-blue-200 p-2 rounded font-mono">
              Previously seen — last order: {foundVehicle.last_order?.order_no || "—"}
              {foundVehicle.customer && <> · Customer: <b>{foundVehicle.customer.display_name}</b></>}
            </div>
          )}

          {/* Registration photo (Stage 2.5) — camera on mobile, upload on desktop */}
          <div className="mt-3">
            <input ref={fileRef} type="file" accept="image/*" capture="environment"
                   onChange={onSelectPhoto} className="hidden"
                   data-testid="ne-reg-photo-input" />
            {!regPhoto ? (
              <button type="button"
                      data-testid="ne-reg-photo-btn"
                      onClick={() => fileRef.current?.click()}
                      className="inline-flex items-center gap-2 h-10 px-4 border border-slate-300 hover:border-blue-500
                                 rounded bg-white text-sm text-slate-700 font-semibold">
                <Camera size={16} /> Take / upload registration photo
              </button>
            ) : (
              <div className="flex items-start gap-3 border border-blue-200 bg-blue-50 rounded p-3"
                   data-testid="ne-reg-photo-preview">
                <img src={regPhoto.previewUrl} alt="Registration"
                     className="w-20 h-20 object-cover rounded border border-blue-300" />
                <div className="flex-1 min-w-0">
                  <div className="label-overline text-blue-800">Registration photo attached</div>
                  <div className="text-xs text-slate-600 truncate">
                    {regPhoto.file.name} · {(regPhoto.file.size / 1024).toFixed(0)} KB
                  </div>
                </div>
                <button type="button" onClick={clearPhoto}
                        data-testid="ne-reg-photo-clear"
                        className="text-slate-500 hover:text-red-600 shrink-0" aria-label="Remove photo">
                  <X size={16} />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Service type */}
        <div>
          <Label className="label-overline">Service type</Label>
          <select data-testid="ne-service-type"
                  className="w-full mt-1 h-12 border border-slate-300 rounded px-2 font-mono"
                  value={serviceId} onChange={(e) => setServiceId(e.target.value)} required>
            {services.length === 0 && <option value="">— No active services configured —</option>}
            {services.map((s) => (
              <option key={s.id} value={s.id}>{s.label} ({s.code}) · unit={s.unit}</option>
            ))}
          </select>
        </div>

        {/* Customer selection */}
        <div>
          <Label className="label-overline">Customer</Label>

          {selectedCustomer ? (
            <div data-testid="ne-selected-customer"
                 className="mt-1 flex items-center gap-3 bg-blue-50 border-2 border-blue-600 rounded p-3">
              <div className="w-10 h-10 bg-blue-700 grid place-items-center rounded">
                <UserCheck size={20} className="text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="label-overline text-blue-800">Selected customer</div>
                <div className="font-semibold text-slate-900 truncate">{selectedCustomer.display_name}</div>
                <div className="font-mono text-xs text-slate-600">{selectedCustomer.customer_code}</div>
              </div>
              <button type="button"
                      data-testid="ne-customer-clear"
                      onClick={clearCustomer}
                      className="text-blue-700 text-sm font-semibold hover:underline shrink-0">
                Change
              </button>
            </div>
          ) : (
            <>
              {!isStaff && (
                <div className="mt-1 relative">
                  <Input
                    data-testid="ne-customer-search"
                    placeholder="Search customer by name or code…"
                    value={custQ}
                    onChange={(e) => setCustQ(e.target.value)}
                    className="rounded h-10"
                  />
                  {customerOptions.length > 0 && (
                    <div className="absolute z-10 w-full bg-white border border-slate-200 rounded shadow-md max-h-56 overflow-y-auto mt-1">
                      {customerOptions.map((c) => (
                        <button type="button"
                                key={c.id}
                                data-testid={`ne-customer-opt-${c.customer_code}`}
                                onClick={() => pickCustomer(c)}
                                className="w-full text-left px-3 py-2 hover:bg-blue-50 border-b border-slate-100 last:border-0">
                          <div className="font-mono text-xs text-slate-500">{c.customer_code}</div>
                          <div className="font-semibold">{c.display_name}</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="mt-3 border-l-4 border-amber-500 bg-amber-50 rounded pl-4 pr-3 py-3">
                <div className="flex items-center gap-2 label-overline text-amber-800">
                  <User size={12} /> Walk-in order (no customer selected)
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-2">
                  <Input data-testid="ne-walkin-driver" placeholder="Driver name" className="rounded h-10 bg-white"
                         value={walkin.driver_name} onChange={(e) => setWalkin({ ...walkin, driver_name: e.target.value })} />
                  <Input data-testid="ne-walkin-company" placeholder="Company" className="rounded h-10 bg-white"
                         value={walkin.company_name} onChange={(e) => setWalkin({ ...walkin, company_name: e.target.value })} />
                  <Input data-testid="ne-walkin-phone" placeholder="Phone" className="rounded h-10 bg-white"
                         value={walkin.phone} onChange={(e) => setWalkin({ ...walkin, phone: e.target.value })} />
                </div>
                {isStaff && (
                  <div className="text-xs text-amber-800 mt-2">
                    Staff cannot create permanent customers — this will be logged as a walk-in.
                    Ask an admin to add this driver to Customers.
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label className="label-overline">Quantity</Label>
            <Input data-testid="ne-quantity" className="rounded h-10 mt-1 font-mono"
                   type="number" step="0.01" value={quantity}
                   onChange={(e) => setQuantity(e.target.value)} placeholder="Optional" />
          </div>
          <div className="md:col-span-2">
            <Label className="label-overline">Entry notes</Label>
            <Textarea data-testid="ne-notes" rows={2} className="rounded mt-1"
                      value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
        </div>

        <div className="flex flex-col-reverse sm:flex-row justify-end gap-3 border-t border-slate-200 pt-4">
          <Button type="button" variant="outline" className="rounded"
                  onClick={() => nav("/app/open-orders")}>Cancel</Button>
          <Button type="submit" data-testid="ne-submit" disabled={submitting}
                  className="rounded bg-blue-700 hover:bg-blue-800 h-11 px-6">
            {submitting ? "Opening…" : "Open Order →"}
          </Button>
        </div>
      </form>
    </div>
  );
}
